import { useEffect, useRef, useState, useCallback } from "react";

export default function useTopScrollbar(dependencies = []) {
  const containerRef = useRef(null); // TableContainer scroll element
  const topScrollRef = useRef(null); // top scrollbar div
  const topInnerRef = useRef(null); // inner spacer div
  const [isDragging, setIsDragging] = useState(false);

  const dragRef = useRef({
    active: false, // true once we've actually started dragging (past threshold)
    pending: false, // pointer is down but we haven't crossed the threshold yet
    startX: 0,
    startScrollLeft: 0,
    pointerId: null,
  });

  // ---- 1. Sync widths & two-way scroll ----
  useEffect(() => {
    const container = containerRef.current;
    const topScroll = topScrollRef.current;
    const topInner = topInnerRef.current;
    if (!container || !topScroll || !topInner) return;

    const syncWidth = () => {
      topInner.style.width = `${container.scrollWidth}px`;
    };

    const onContainerScroll = () => {
      if (dragRef.current.active) return;
      if (topScroll.scrollLeft !== container.scrollLeft) {
        topScroll.scrollLeft = container.scrollLeft;
      }
    };

    const onTopScroll = () => {
      if (dragRef.current.active) return;
      if (container.scrollLeft !== topScroll.scrollLeft) {
        container.scrollLeft = topScroll.scrollLeft;
      }
    };

    syncWidth();
    container.addEventListener("scroll", onContainerScroll, { passive: true });
    topScroll.addEventListener("scroll", onTopScroll, { passive: true });

    const ro = new ResizeObserver(syncWidth);
    ro.observe(container);
    if (container.firstElementChild) ro.observe(container.firstElementChild);

    return () => {
      container.removeEventListener("scroll", onContainerScroll);
      topScroll.removeEventListener("scroll", onTopScroll);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // ---- 2. Pointer-based drag ----
  const onPointerDown = useCallback((e) => {
    // Only left mouse button (or touch/pen)
    if (e.pointerType === "mouse" && e.button !== 0) return;

    // Ignore clicks on interactive elements so buttons/links still work
    const target = e.target;
    if (
      target.closest(
        "button, a, input, select, textarea, [role='button'], .MuiIconButton-root",
      )
    ) {
      return;
    }

    const container = containerRef.current;
    const topScroll = topScrollRef.current;
    if (!container || !topScroll) return;

    // If the click came from the top scrollbar, skip the native scrollbar thumb zone
    if (e.currentTarget === topScroll) {
      const rect = topScroll.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const trackHeight = topScroll.offsetHeight - topScroll.clientHeight;
      if (trackHeight > 0 && y > topScroll.clientHeight) return;
    }

    dragRef.current = {
      active: false, // not yet — wait for movement threshold
      pending: true,
      startX: e.clientX,
      startScrollLeft: container.scrollLeft,
      pointerId: e.pointerId,
    };

    try {
      container.setPointerCapture(e.pointerId);
    } catch (_) {}
  }, []);

  const onPointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag.pending && !drag.active) return;

    const container = containerRef.current;
    const topScroll = topScrollRef.current;
    if (!container || !topScroll) return;

    const delta = e.clientX - drag.startX;

    // Not active yet — check if we crossed the drag threshold
    if (!drag.active) {
      if (Math.abs(delta) < 5) return; // under threshold: still treat as a click
      drag.active = true;
      setIsDragging(true);
    }

    const newScrollLeft = drag.startScrollLeft - delta;

    topScroll.scrollLeft = newScrollLeft;
    container.scrollLeft = newScrollLeft;

    e.preventDefault();
  }, []);

  const endDrag = useCallback(() => {
    const drag = dragRef.current;
    if (!drag.pending && !drag.active) return;

    drag.active = false;
    drag.pending = false;
    setIsDragging(false);

    const container = containerRef.current;
    if (container && drag.pointerId != null) {
      try {
        container.releasePointerCapture(drag.pointerId);
      } catch (_) {}
    }
    dragRef.current.pointerId = null;
  }, []);

  // Attach pointer listeners to BOTH the top scrollbar and the table container
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const container = containerRef.current;
    if (!topScroll || !container) return;

    topScroll.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    return () => {
      topScroll.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, [onPointerDown, onPointerMove, endDrag]);

  return { containerRef, topScrollRef, topInnerRef, isDragging };
}
